import { Link, useRouterState, useNavigate } from "@tanstack/react-router";
import { User, Package, MapPin, Heart, LogOut, Shield, Loader2, LayoutDashboard, Sparkles, ChevronLeft } from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { AnnouncementBar } from "@/components/home/AnnouncementBar";
import { Navbar } from "@/components/home/Navbar";
import { Footer } from "@/components/home/Footer";
import { useAuth } from "@/lib/auth";

type AccountNav = {
  to: string;
  label: string;
  icon: typeof User;
  exact?: boolean;
};

const nav: AccountNav[] = [
  { to: "/account", label: "Overview", icon: LayoutDashboard, exact: true },
  { to: "/account/profile", label: "Profile", icon: User },
  { to: "/account/addresses", label: "Addresses", icon: MapPin },
  { to: "/account/wishlist", label: "Wishlist", icon: Heart },
];

export function AccountLayout({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { user, role, loading, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate({ to: "/auth", search: { redirect: undefined } });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-primary">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-gold" />
          <p className="eyebrow">Loading account...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const rawName = user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split("@")[0] || "Valued Member";
  const name = rawName.charAt(0).toUpperCase() + rawName.slice(1);
  const initials = name.charAt(0).toUpperCase();
  const isUserAdmin = role === "admin";
  const memberTier = isUserAdmin ? "Atelier Master Admin" : "Royal Circle VIP";

  return (
    <div className="min-h-screen bg-background text-foreground font-sans pb-24 lg:pb-0">
      <AnnouncementBar />
      <Navbar />

      {/* Desktop Hero Banner */}
      <section className="hidden lg:block relative bg-primary text-cream -mt-20 pt-32 pb-14 overflow-hidden">
        <div className="absolute inset-0 opacity-25 bg-[radial-gradient(circle_at_25%_35%,var(--gold)_0%,transparent_55%)] pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 flex items-center justify-between">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-gold/80 flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-gold" /> Home · Account Sanctuary
            </div>
            <h1 className="mt-3 font-display text-4xl lg:text-5xl">
              Welcome back, <span className="italic text-gold">{name.split(" ")[0]}</span>
            </h1>
            <p className="mt-2 text-sm text-cream/70 max-w-lg leading-relaxed">
              Manage your handcrafted orders, saved candle rituals, delivery addresses, and personal atelier profile.
            </p>
          </div>
          
          <div className="bg-card/10 backdrop-blur-md border border-gold/20 rounded-[20px] px-6 py-4 text-right hidden xl:block">
            <div className="text-[0.65rem] tracking-[0.25em] uppercase text-gold/90">Membership Status</div>
            <div className="mt-1 font-display text-xl text-cream font-semibold flex items-center gap-2 justify-end">
              <Sparkles className="h-4 w-4 text-gold fill-gold/20" /> {memberTier}
            </div>
            <div className="mt-1 text-[0.7rem] text-cream/60">{user.email}</div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10 py-4 lg:py-16">
        <div className="grid lg:grid-cols-[280px_1fr] gap-10 lg:gap-14">
          
          {/* Desktop Left Sidebar */}
          <aside className="hidden lg:block space-y-6">
            <div className="bg-card rounded-[20px] p-6 shadow-luxe border border-border/60 relative overflow-hidden">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-full bg-gradient-to-br from-gold via-gold/80 to-gold/40 grid place-items-center text-primary font-display text-xl font-bold shadow-md border-2 border-background">
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg font-bold text-primary truncate">{name}</div>
                  <div className="text-[0.62rem] font-semibold tracking-[0.2em] uppercase text-gold mt-0.5">
                    {memberTier}
                  </div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">{user.email}</div>
                </div>
              </div>
            </div>

            <nav className="bg-card rounded-[20px] p-3 shadow-luxe border border-border/60 space-y-1">
              <div className="px-4 py-2 text-[0.6rem] font-semibold tracking-[0.25em] uppercase text-muted-foreground">
                Account Sanctuary
              </div>
              {nav.map((n) => {
                const active = n.exact ? pathname === n.to : pathname.startsWith(n.to);
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    className={`flex items-center gap-3 px-4 py-3 rounded-[12px] text-sm font-medium transition-all ${
                      active 
                        ? "bg-primary text-gold font-semibold shadow-sm border border-gold/20" 
                        : "text-primary hover:bg-secondary/70 hover:text-gold"
                    }`}
                  >
                    <n.icon className={`h-4 w-4 ${active ? "text-gold" : "text-muted-foreground"}`} strokeWidth={1.8} />
                    <span>{n.label}</span>
                  </Link>
                );
              })}
              
              {isUserAdmin && (
                <Link
                  to="/admin"
                  className="flex items-center gap-3 px-4 py-3 rounded-[12px] text-sm font-medium text-gold bg-gold/5 hover:bg-gold/10 transition-colors border border-gold/20"
                >
                  <Shield className="h-4 w-4" strokeWidth={1.8} />
                  <span>Admin Portal</span>
                </Link>
              )}

              <div className="pt-2 border-t border-border/60">
                <button
                  onClick={signOut}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-[12px] text-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors text-left font-medium"
                >
                  <LogOut className="h-4 w-4" strokeWidth={1.8} />
                  <span>Sign Out</span>
                </button>
              </div>
            </nav>
          </aside>

          {/* Main Content Area */}
          <div>
            {/* Sub-page mobile back header */}
            {pathname !== "/account" && (
              <div className="lg:hidden flex items-center gap-3 pb-3 mb-4 border-b border-border/80">
                <Link to="/account" aria-label="Back to Account" className="h-9 w-9 rounded-full bg-secondary grid place-items-center text-primary hover:bg-gold/20 transition-colors shrink-0">
                  <ChevronLeft className="h-5 w-5" />
                </Link>
                <div className="min-w-0 flex-1">
                  <h2 className="font-display text-lg font-bold text-primary leading-tight">{title}</h2>
                  {subtitle && <p className="text-[0.7rem] text-muted-foreground line-clamp-1">{subtitle}</p>}
                </div>
              </div>
            )}

            {/* Desktop page title */}
            <div className="hidden lg:flex pb-4 lg:pb-6 border-b border-border/80 items-baseline justify-between">
              <div>
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl text-primary font-bold">{title}</h2>
                {subtitle && <p className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed">{subtitle}</p>}
              </div>
            </div>

            <div className="mt-2 lg:mt-8">{children}</div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
